/**
 * Shop-local wall clock → epoch seconds.
 *
 * A booking slot is a time in the *shop's* timezone, not the browser's. Building
 * the timestamp with `new Date("2026-09-01 09:00:00")` uses whatever zone the
 * customer's laptop is set to, so a customer in New York booking a 09:00 slot at
 * a Chicago shop files an 08:00 appointment — and now that the API validates the
 * slot, that booking is rejected outright rather than silently landing an hour
 * out.
 *
 * Mirrors src/common/shop-time.ts on the backend, which does the inverse.
 */

/**
 * One formatter per zone. Building an Intl.DateTimeFormat costs about ten
 * times what formatting with it does, and list screens read hundreds of
 * appointments through these helpers on every render.
 */
const wallClockFormats = new Map<string, Intl.DateTimeFormat>();

function wallClockFormat(timeZone: string): Intl.DateTimeFormat {
  let format = wallClockFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    wallClockFormats.set(timeZone, format);
  }
  return format;
}

/** How far `timeZone` is ahead of UTC, in ms, at the given instant. */
function zoneOffsetMs(timeZone: string, instant: Date): number {
  const parts = wallClockFormat(timeZone).formatToParts(instant);

  const read = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  // Intl renders midnight as hour 24 in some locales; Date.UTC wants 0.
  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second')
  );

  return asIfUtc - instant.getTime();
}

/**
 * Epoch seconds for a wall-clock date and time as read in `timeZone`.
 *
 * @param date  "YYYY-MM-DD"
 * @param time  "HH:mm" or "HH:mm:ss"
 * @param timeZone IANA zone. Falls back to the browser's zone when absent, which
 *   is the old behaviour and only correct when the viewer sits in the shop's zone.
 */
export function shopWallClockToEpoch(
  date: string,
  time: string,
  timeZone?: string | null
): number {
  const [year, month, day] = date.split('-').map(Number);
  const [hour = 0, minute = 0, second = 0] = time.split(':').map(Number);

  if (!timeZone) {
    return Math.floor(
      new Date(year, month - 1, day, hour, minute, second).getTime() / 1000
    );
  }

  const asIfUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  // The offset depends on the instant we are still solving for, so guess with
  // the offset at the naive instant, then correct once. The second pass is what
  // handles a date sitting on a DST changeover.
  const firstGuess = asIfUtc - zoneOffsetMs(timeZone, new Date(asIfUtc));
  const refined = asIfUtc - zoneOffsetMs(timeZone, new Date(firstGuess));

  return Math.floor(refined / 1000);
}

/** "YYYY-MM-DD" for a Date, read in the browser's zone — never via toISOString. */
export function toDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * A Date whose *local* fields read the shop's wall clock at `epochSeconds`.
 *
 * Admin screens format and group appointments with date-fns, which reads a
 * Date's local fields — so `new Date(epoch * 1000)` showed a Chicago shop's
 * 07:30 booking as 18:00 to anyone whose laptop is in India. Building the Date
 * from the shop-zone fields instead keeps every existing format()/isSameDay()
 * call correct for any viewer.
 *
 * For display and grouping only: never turn the result back into an epoch
 * (use shopWallClockToEpoch for that).
 */
export function shopWallClockDate(epochSeconds: number, timeZone?: string | null): Date {
  const instant = new Date(Number(epochSeconds) * 1000);
  if (!timeZone) return instant;

  const parts = wallClockFormat(timeZone).formatToParts(instant);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');

  return new Date(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second')
  );
}

/**
 * "14:30" or "14:30:00" — a slot, business-hours or legacy appointmentTime
 * value — as US 12-hour time: "2:30 PM". Display only; the API keeps 24-hour
 * strings, so never send this back.
 */
export function formatClockTime(time: string): string {
  const [hour, minute] = time.split(':').map(Number);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) {
    return time;
  }
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 === 0 ? 12 : hour % 12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/**
 * Today's date on the shop's clock, at midnight, for date pickers — a
 * customer whose laptop is a day ahead of the shop must not be offered (or
 * denied) the wrong day. Falls back to the browser's date without a zone.
 */
export function shopToday(timeZone?: string | null): Date {
  const now = shopWallClockDate(Math.floor(Date.now() / 1000), timeZone);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** The shop zone's short name at an instant ("CDT"), so a time can say which clock it is on. */
export function shopZoneAbbreviation(timeZone?: string | null, at: Date = new Date()): string {
  if (!timeZone) return '';
  return (
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}
